import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AlertCircle, RefreshCw, Users, CreditCard, Heart, Mail } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const TermsOfServicePage = () => {
  const keyTerms = [
    { icon: Heart, text: 'Connect is for wellness, not medical use' },
    { icon: RefreshCw, text: 'Features and offerings may evolve over time' },
    { icon: AlertCircle, text: 'Early access does not guarantee availability' },
    { icon: CreditCard, text: 'Pricing and memberships may change' },
    { icon: Users, text: 'Shared features require appropriate consent' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFCFA]" data-testid="terms-of-service-page">
      <Navigation />

      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            
            {/* Header Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-10 md:mb-14"
            >
              <Link 
                to="/" 
                className="inline-flex items-center gap-1.5 text-sm text-[#7A7A7A] hover:text-[#1A1A1A] transition-colors mb-8"
              >
                <span>←</span>
                <span>Back to Home</span>
              </Link>
              
              <div className="max-w-2xl">
                <p className="text-sm uppercase tracking-widest text-[#8A8A8A] mb-3">Legal</p>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4 leading-tight">
                  Terms of Service
                </h1>
                <p className="text-[#7A7A7A] mb-6">Last Updated: March 16, 2026</p>
                <p className="text-lg text-[#4A4A4A] leading-relaxed">
                  These terms govern your use of Connect Band products and services. By using Connect, you agree to these terms.
                </p>
              </div>
            </motion.div>

            {/* Key Terms Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-16 md:mb-20"
            >
              <div className="bg-gradient-to-br from-white to-[#FAF8F5] rounded-3xl border border-black/5 p-8 md:p-10 shadow-sm">
                <h2 className="text-lg font-semibold text-[#1A1A1A] mb-6">Key Terms at a Glance</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {keyTerms.map((term, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#F5F0EB] flex items-center justify-center flex-shrink-0">
                        <term.icon className="w-4 h-4 text-[#6A6A6A]" />
                      </div>
                      <p className="text-[#4A4A4A] text-sm leading-relaxed pt-1.5">{term.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <div className="space-y-14 md:space-y-18">
              
              {/* Eligibility & Accounts */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">Eligibility</h2>
                    <p className="text-[#4A4A4A] leading-relaxed mb-4">
                      You may use Connect Services only if you are legally able to enter into a binding agreement under applicable law.
                    </p>
                    <p className="text-[#5A5A5A] text-sm">
                      If you use the Services on behalf of another person or organization, you represent that you have authority to do so.
                    </p>
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">Your Account</h2>
                    <p className="text-[#4A4A4A] leading-relaxed mb-4">
                      Some features may require an account. You are responsible for providing accurate information and keeping your credentials secure.
                    </p>
                    <p className="text-[#5A5A5A] text-sm">
                      We may suspend accounts for misuse or violation of these Terms.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Early Access */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Early Access & Reservations
                </h2>
                <div className="bg-[#FAF8F5] rounded-2xl p-6 md:p-8 mb-6">
                  <p className="text-[#4A4A4A] leading-relaxed">
                    Connect may offer early access, waitlist registration, and reservation opportunities. Submitting a reservation does not guarantee product availability, delivery timing, or specific features unless explicitly confirmed.
                  </p>
                </div>
                <p className="text-[#5A5A5A] text-sm">
                  We may modify, pause, or discontinue early access programs and launch promotions at any time.
                </p>
              </motion.section>

              {/* Pricing */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Pricing & Membership
                </h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    { title: 'Launch Pricing', desc: 'Displayed pricing is subject to change until confirmed at purchase.' },
                    { title: 'Subscriptions', desc: 'Membership services may renew per the terms at signup unless canceled.' },
                    { title: 'Evolving Plans', desc: 'Post-launch pricing structures may evolve as Connect develops.' },
                  ].map((item, index) => (
                    <div key={index} className="bg-white rounded-2xl border border-black/5 p-5">
                      <h3 className="font-medium text-[#1A1A1A] mb-2 text-sm">{item.title}</h3>
                      <p className="text-[#5A5A5A] text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* Health Disclaimer */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Health & Wellness Disclaimer
                </h2>
                <div className="bg-gradient-to-br from-rose-50 to-[#FAF8F5] rounded-2xl border border-rose-100 p-6 md:p-8">
                  <p className="font-medium text-[#1A1A1A] mb-4">
                    Connect is intended for general wellness, informational, and lifestyle purposes only.
                  </p>
                  <div className="space-y-3 text-[#4A4A4A] text-sm">
                    <p>Connect is not a medical device unless explicitly stated. The Services do not provide medical advice, diagnosis, treatment, or emergency response.</p>
                    <p>Do not rely on Connect as a substitute for professional medical judgment or emergency assistance. In an emergency, contact appropriate emergency services immediately.</p>
                  </div>
                </div>
              </motion.section>

              {/* Family & Shared */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Family & Shared Experiences
                </h2>
                <p className="text-[#4A4A4A] leading-relaxed mb-6 max-w-2xl">
                  Some Connect experiences involve multiple users. By participating, you understand:
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    'Information may be shared among authorized participants',
                    'Visibility depends on settings and permissions',
                    'Use shared features thoughtfully and responsibly',
                    'Parental involvement required for minors',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-black/5">
                      <div className="w-6 h-6 rounded-full bg-[#F0EBE5] flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-[#8A8A8A]">{index + 1}</span>
                      </div>
                      <p className="text-[#4A4A4A] text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* Acceptable Use */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Acceptable Use
                </h2>
                <p className="text-[#4A4A4A] leading-relaxed mb-6">
                  You agree not to misuse Connect Services. This includes:
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    'Using Services in violation of law',
                    'Interfering with operation or security',
                    'Gaining unauthorized access',
                    'Reverse engineering without permission',
                    'Harassing or harming others',
                    'Submitting fraudulent information',
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2.5 text-[#5A5A5A] text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C5C0BA]" />
                      {item}
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* IP & Third Party */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Intellectual Property</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      The Connect brand, designs, content, and materials are owned by Connect or its licensors and protected by intellectual property laws. Use without permission is prohibited.
                    </p>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Third-Party Services</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      Services may link to third-party content. We don't control those third parties and their use is governed by their own terms and policies.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Changes & Termination */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Service Changes</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      We may update, modify, suspend, or discontinue any part of the Services at any time. Connect is evolving, and features may change.
                    </p>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Termination</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      You may stop using Services anytime. We may suspend or terminate access for violations or if we discontinue Services.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Disclaimers & Liability */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6">
                  Disclaimers & Limitations
                </h2>
                <div className="space-y-4">
                  <div className="p-5 bg-[#FAF8F5] rounded-2xl">
                    <h3 className="font-medium text-[#1A1A1A] mb-2">Services Provided "As Is"</h3>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed">
                      To the extent permitted by law, Services are provided without warranties. We aim for reliability but cannot guarantee uninterrupted or error-free operation.
                    </p>
                  </div>
                  <div className="p-5 bg-[#FAF8F5] rounded-2xl">
                    <h3 className="font-medium text-[#1A1A1A] mb-2">Limitation of Liability</h3>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed">
                      To the extent permitted by law, Connect is not liable for indirect or consequential damages. Total liability is limited to amounts paid in the twelve months before the claim, or $100, whichever is greater.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Governing Law & Updates */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-t border-black/5 pt-10"
              >
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Governing Law</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles, unless another jurisdiction is required.
                    </p>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">Terms Updates</h2>
                    <p className="text-[#4A4A4A] leading-relaxed">
                      We may update these Terms. Material changes will be communicated. Continued use after changes means you accept the updated Terms.
                    </p>
                  </div>
                </div>
              </motion.section>

            </div>

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mt-20 md:mt-24"
            >
              <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] rounded-3xl p-8 md:p-12 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3">
                  Questions about these terms?
                </h2>
                <p className="text-white/70 mb-8 max-w-md mx-auto">
                  Reach out to our team anytime. We're here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="mailto:hello@connectband.health"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#1A1A1A] rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    hello@connectband.health
                  </a>
                  <a 
                    href="mailto:legal@connectband.health"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    legal@connectband.health
                  </a>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
